import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSearchParams, Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { motion } from 'framer-motion'
import { Search, Hash, User, MessageSquare } from 'lucide-react'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import api from '@/api/client'

export default function SearchPage() {
  const { t } = useTranslation()
  const [searchParams] = useSearchParams()
  const query = searchParams.get('q') || ''
  const [activeTab, setActiveTab] = useState<'posts' | 'users' | 'topics'>('posts')

  const { data, isLoading } = useQuery({
    queryKey: ['search', query],
    queryFn: () => api.get(`/search?q=${encodeURIComponent(query)}`).then(r => r.data),
    enabled: query.length >= 2
  })

  return (
    <div className="max-w-2xl mx-auto px-4 py-6">
      <h1 className="font-display text-2xl font-bold mb-6">
        {t('search.resultsFor', { query })}
      </h1>

      <div className="flex gap-2 mb-6">
        {[
          { key: 'posts', label: t('search.posts'), icon: MessageSquare },
          { key: 'users', label: t('search.users'), icon: User },
          { key: 'topics', label: t('search.topics'), icon: Hash },
        ].map(tab => (
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === tab.key}
            aria-label={tab.label}
            key={tab.key}
            onClick={() => setActiveTab(tab.key as any)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium ${
              activeTab === tab.key ? 'bg-primary text-white' : 'bg-secondary text-muted-foreground'
            }`}
          >
            <tab.icon className="w-4 h-4" /> {tab.label}
          </button>
        ))}
      </div>

        {isLoading && (
          <div className="text-center py-12 text-muted-foreground">
            <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            {t('search.searching')}
          </div>
        )}

      {!isLoading && <div className="space-y-3">
        {activeTab === 'posts' && data?.posts?.map((post: any) => (
          <Link key={post._id} to={`/post/${post._id}`}>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="p-4 rounded-xl bg-card border border-border hover:border-primary/30 transition-colors">
              <p className="text-sm font-medium">{post.username}</p>
              <p className="text-sm text-muted-foreground line-clamp-2">{post.message}</p>
            </motion.div>
          </Link>
        ))}

        {activeTab === 'users' && data?.users?.map((u: any) => (
          <Link key={u._id} to={`/profile/${u.username}`}>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex items-center gap-3 p-4 rounded-xl bg-card border border-border hover:border-primary/30 transition-colors">
              <Avatar className="w-10 h-10"><AvatarImage src={u.avatar} /><AvatarFallback>{u.username[0]}</AvatarFallback></Avatar>
              <div>
                <p className="font-medium">{u.username}</p>
                <p className="text-xs text-muted-foreground">{u.bio?.slice(0, 50)}</p>
              </div>
            </motion.div>
          </Link>
        ))}

        {activeTab === 'topics' && data?.topics?.map((topic: any) => (
          <Link key={topic._id} to={`/forum/${topic._id}`}>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="p-4 rounded-xl bg-card border border-border hover:border-primary/30 transition-colors">
              <p className="font-medium">{topic.title}</p>
              <p className="text-sm text-muted-foreground line-clamp-2">{topic.content}</p>
            </motion.div>
          </Link>
        ))}
      </div>}

      {!isLoading && data && !data[activeTab]?.length && (
        <div className="text-center py-12 text-muted-foreground">
          <Search className="w-12 h-12 mx-auto mb-4 opacity-50" />
          <p>{t('common.noResults')}</p>
        </div>
      )}
    </div>
  )
}
